import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, CircularProgress, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import ChannelList from '../ChannelList';
import ChatPanel from '../ChatPanel';
import ChannelSettings from '../ChannelSettings';
import { useSendbirdStateContext } from '@sendbird/uikit-react/useSendbirdStateContext';
import { useDispatch, useSelector } from 'react-redux';
import { channelUrlSelector, setChannelUrlAction, setTargetMessageAction } from 'domain/chat';
import useClearOpenChannelOnRemoval from '../../hooks/useClearOpenChannelOnRemoval';
import useChatUnreadCounters from '../../hooks/useChatUnreadCounters';

interface Props {
  onClose: () => void;
}

const ChatLayout: React.FC<Props> = ({ onClose }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const channelUrl = useSelector(channelUrlSelector);
  const [showSettings, setShowSettings] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const state = useSendbirdStateContext();

  const setChannelUrl = useCallback(
    (url: string) => {
      dispatch(setChannelUrlAction(url));
      // On deselect, also clear the jump-to-message target so a later channel open
      // doesn't inherit a stale startingPoint/animatedMessageId (parity with the mobile
      // handleBackToList). DA-19493.
      if (!url) {
        dispatch(setTargetMessageAction({ id: null, timestamp: null }));
      }
      setShowSettings(false);
    },
    [dispatch],
  );

  // Deselect the open conversation when the current user loses access to it
  // (removed from the group, left, or the channel was deleted). DA-19493.
  useClearOpenChannelOnRemoval(channelUrl, setChannelUrl);

  // Keep the three chat filter counters (All / current company / current document)
  // in sync with the live Sendbird channel collection while the drawer is open.
  useChatUnreadCounters();

  useEffect(() => {
    document.body.classList.add(`sendbird-theme--${state.config.theme}`);
    return () => {
      document.body.classList.remove(`sendbird-theme--${state.config.theme}`);
    };
  }, [state.config.theme]);

  return (
    <Stack direction="row" sx={{ height: '100vh', overflow: 'hidden' }}>
      <ChannelList selectedChannelUrl={channelUrl} onSelect={setChannelUrl} onCreatingChange={setIsCreating} />

      {/* Conversation area. The creation loader (DA-19646) overlays THIS region — the message
          history, or the no-chat-selected block — rather than a full-screen backdrop, per the
          Figma design (node 22000-134537). `position: relative` anchors the absolute overlay. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        {showSettings && channelUrl ? (
          <Stack flex={1} direction="column">
            <ChannelSettings channelUrl={channelUrl} onCloseClick={() => setShowSettings(false)} />
          </Stack>
        ) : (
          <ChatPanel channelUrl={channelUrl} onToggleSettings={setShowSettings} onClose={onClose} />
        )}

        {isCreating && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              backgroundColor: (theme) => theme.palette.chat?.surface ?? 'rgba(251, 253, 255, 1)',
              zIndex: (theme) => theme.zIndex.modal,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {formatMessage({ id: 'chat.userDirectory.creatingChat', defaultMessage: 'Creating chat' })}
            </Typography>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default ChatLayout;
